import type {ReactElement} from 'react';
import React from 'react';
import {HexPicker} from '@Components/color-picker-v2/components/hex-picker';
import {ColorFill, ColorFillPropertyType} from '@Components/color-fill';
import type {ColorStorage} from '@Components/color-picker-v2';
import {PickerInputField} from '@Components/color-picker-v2/components/picker-input-field';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import type {RGBA} from '@Utils/color.util';
import {ThumbnailItem, ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import styles from './picker-section.module.scss';
import {AlphaPercentagePickerInputField} from '@Components/color-picker-v2/components/alpha-percentage-picker-input-field/alpha-percentage-picker-input-field';

interface PickerSectionProps {
  color: ColorStorage[];
  type?: ColorFillPropertyType;

  onChangeColor(color: ColorStorage[]): void;

  onChangeEnded(color: ColorStorage[]): void;

  selectedIndex?: number;

  onChangeSelectedIndex(index: number): void;

  isAlphaDisabled?: boolean;
}

const IS_PICKER_ENABLED = false;

export function PickerSection({selectedIndex = 0, isAlphaDisabled = false, type = ColorFillPropertyType.SOLID, ...props}: PickerSectionProps): ReactElement | null {
  const getColor = (): RGBA => {
    return props.color[selectedIndex].color;
  };

  const getUpdatedColorObject = (color: RGBA): ColorStorage[] => {
    const newColor = props.color;
    newColor[selectedIndex].color = color;
    return newColor;
  };

  const onChangeColor = (color: RGBA | null): void => {
    if (color === null) {
      return;
    }
    props.onChangeColor(getUpdatedColorObject(color));
  };
  const onChangeColorEnded = (color: RGBA | null): void => {
    if (color === null) {
      return;
    }
    props.onChangeEnded(getUpdatedColorObject(color));
  };

  const getColorThumbnails = (): ReactElement | null => {
    if (type === ColorFillPropertyType.SOLID || props.color.length <= 1) {
      return null;
    }
    return (
      <div className={`flex-center spacing-m-t-4 ${styles.gap}`}>
        <ThumbnailItem
          className="spacing-p-r-2 spacing-p-l-2"
          onClick={(): void => {
            props.onChangeSelectedIndex(0);
          }}
          isSelected={selectedIndex === 0}
          thumbnail={<ColorFill color={props.color[0].color} height={24} width={24} />}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_color_one')}
        />
        <ThumbnailItem
          className="spacing-p-r-2 spacing-p-l-2"
          onClick={(): void => {
            props.onChangeSelectedIndex(1);
          }}
          isSelected={selectedIndex === 1}
          thumbnail={<ColorFill color={props.color[1].color} height={24} width={24} />}
          selectionState={ThumbnailItemSelectionState.BORDER}
          text={window.i18next.t('pmwjs_color_two')}
        />
      </div>
    );
  };

  const getHexPicker = (): ReactElement => {
    return (
      <>
        <div className={`flex-h-row ${styles.gap} spacing-m-b-2`}>
          <div>
            <ColorFill color={getColor()} width={40} height={40} />
          </div>
          <div className={`${styles.gap} _full-width flexbox`}>
            <div className={!isAlphaDisabled ? styles.pickerInput : '_full-width'}>
              <PickerInputField
                color={getColor()}
                onChangeColor={(color): void => {
                  onChangeColor(color as RGBA);
                }}
                onChangeEnded={(color): void => {
                  onChangeColorEnded(color as RGBA);
                }}
                tooltipClasses={styles.tooltip}
                isMobileVersion
              />
              {IS_PICKER_ENABLED ? (
                <div>
                  <Icon icon="icon-colorpicker" shape={IconShape.SQUARE} size={IconSize.SIZE_ICON_20} type={IconType.SECONDARY} />
                </div>
              ) : null}
            </div>

            {!isAlphaDisabled ? (
              <div className={styles.alphaInput}>
                <AlphaPercentagePickerInputField
                  color={getColor()}
                  onChangeColor={(color): void => {
                    onChangeColor(color);
                  }}
                  onChangeEnded={(color): void => {
                    onChangeColorEnded(color);
                  }}
                  inputClassName={styles.alphaInputField}
                />
              </div>
            ) : (
              []
            )}
          </div>
        </div>
        <HexPicker
          color={props.color[selectedIndex].color}
          onChange={(color: RGBA): void => {
            onChangeColor(color);
          }}
          onChangeEnded={(color: RGBA): void => {
            onChangeColorEnded(color);
          }}
          isAlphaDisabled={isAlphaDisabled}
        />
        {getColorThumbnails()}
      </>
    );
  };

  return getHexPicker();
}
